import {IS_TEST} from "@/app/util/environment";
import type {LogApi} from "@aops-trove/fast-server-common";
import {pickBy} from "lodash-es";
import pino from "pino";
import {addPinoSyslogFormatter} from "@aops-trove/fast-log";

const FIELDS_TO_REDACT = [
	"body.password",
	"body.user.password",
	"body.update.password",
	"body.currentPassword",
	"body.users[*].password",
	"headers['X-Auth-Key']",
	"headers['X-Auth-Email']",
	"headers['x-api-key']",
	"headers.Authorization",
];

const IS_DEV = process.env.NODE_ENV !== "production";

/**
 * Creates a logger using the fast-server-common helper. For instructions on using the logger, see
 * https://aops.slab.com/posts/fastack-how-to-logging-on-a-server-l9bdjrvq#hb4r8-when-writing-new-code
 *
 * The logger will pretty print in development and raw JSON in production.
 */
// Pretty print in development
const BASE_DEV_LOG_CONFIG = {
	transport: {
		target: "pino-pretty",
		options: {
			colorize: true,
		},
	},
	level: "debug",
	redact: FIELDS_TO_REDACT,
};

// JSON in production
const BASE_PROD_LOG_CONFIG = {
	level: "info",
	redact: FIELDS_TO_REDACT,
	base: {
		dd: {
			service: process.env.OTEL_SERVICE_NAME,
			version: process.env.NEXT_PUBLIC_BUILD_VERSION,
			env: process.env.NEXT_PUBLIC_ENVIRONMENT_STRING,
		},
	},
};

const logConfig = IS_DEV ? BASE_DEV_LOG_CONFIG : BASE_PROD_LOG_CONFIG;
const logConfigWithSyslogFormatter = addPinoSyslogFormatter(logConfig);

export const log: LogApi = pino(logConfigWithSyslogFormatter);

/**
 * This is a copy of the function from fast-server-common.
 * It's copied here because we can't import from fast-server-common in middleware:
 * https://github.com/aops-ba/academy2/actions/runs/11981886823/job/33408943558
 */
export function makeLoggableError(e: any): object {
	return pickBy(
		{
			message: e.message,
			name: e.name,
			type: e.type,
			code: e.code,
			cause: e.cause,
			status: e.status,
			stack: e.stack,
			moreInfo: e.moreInfo,
		},
		(a) => a !== undefined
	);
}

// Mock logs in tests, since the pino logger errors out in CI
// Remember to clear the mocks before each test if you want to do any assertions on the calls
if (IS_TEST) {
	log.debug = jest.fn();
	log.info = jest.fn();
	log.warn = jest.fn();
	log.error = jest.fn();
	log.fatal = jest.fn();
	log.trace = jest.fn();
}
